import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, Edit3, UserPlus, Check, Award, Trash2, ShieldAlert } from "lucide-react";
import Layout from "@/components/Layout";
import PostCard from "@/components/PostCard";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const NICHES = ["affiliate", "dropshipping", "freelance", "crypto", "content", "health", "ai"];

export default function Profile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", bio: "", niches: [], is_mentor: false, mentor_specialty: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: ps }] = await Promise.all([
        api.get(`/users/${id}`),
        api.get(`/posts/user/${id}`),
      ]);
      setProfile(p);
      setPosts(ps);
      setForm({
        name: p.name || "",
        bio: p.bio || "",
        niches: p.niches || [],
        is_mentor: !!p.is_mentor,
        mentor_specialty: p.mentor_specialty || "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const toggleFollow = async () => {
    const { data } = await api.post(`/users/${id}/follow`);
    setProfile((p) => ({
      ...p,
      is_following: data.following,
      followers: data.following
        ? [...(p.followers || []), me.user_id]
        : (p.followers || []).filter((f) => f !== me.user_id),
    }));
  };

  const save = async () => {
    const { data } = await api.patch("/users/me", form);
    setProfile((p) => ({ ...p, ...data }));
    setUser(data);
    setEditing(false);
  };

  const adminDeleteUser = async () => {
    if (!window.confirm(`Permanently delete ${profile.name}'s account and all their content? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${profile.user_id}`);
      navigate("/feed");
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to delete user.");
    }
  };

  const toggleNiche = (n) => {
    setForm((f) => ({
      ...f,
      niches: f.niches.includes(n) ? f.niches.filter((x) => x !== n) : [...f.niches, n],
    }));
  };

  if (loading || !profile) {
    return <Layout><div className="max-w-4xl mx-auto py-8 px-4 md:px-8"><div className="surface rounded-xl h-72 shimmer" /></div></Layout>;
  }

  const isMe = me?.user_id === profile.user_id;
  const verifiedEarnings = profile.verified_earnings || profile.total_earnings || 0;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto py-8 px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="surface rounded-xl p-6 mb-5"
          data-testid="profile-header"
        >
          <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
            <div className="flex items-center gap-4">
              <img
                src={profile.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${profile.user_id}`}
                alt=""
                className="w-20 h-20 rounded-lg object-cover bg-white/5"
              />
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="font-heading font-bold text-3xl tracking-tight">{profile.name}</h1>
                  {profile.is_admin && (
                    <span className="px-2 py-1 rounded text-[10px] font-mono uppercase tracking-wider flex items-center gap-1 bg-red-500/10 border border-red-500/30 text-red-300">
                      <ShieldAlert size={11} strokeWidth={2.5} /> Admin
                    </span>
                  )}
                  {profile.is_mentor && (
                    <span className="verified-pill text-[10px] font-mono px-2 py-1 rounded uppercase tracking-wider flex items-center gap-1">
                      <BadgeCheck size={11} strokeWidth={2.5} /> Mentor
                    </span>
                  )}
                </div>
                <div className="text-sm text-white/55 font-mono lowercase mt-1 flex flex-wrap gap-1.5">
                  {(profile.niches || []).map((n) => (
                    <span key={n} className="px-2 py-0.5 rounded bg-white/5 text-xs uppercase tracking-wider">{n}</span>
                  ))}
                  {(!profile.niches || profile.niches.length === 0) && (
                    <span className="text-white/30 text-xs">no niches yet</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isMe ? (
                <button
                  data-testid="edit-profile-btn"
                  onClick={() => setEditing(!editing)}
                  className="surface rounded-md px-4 py-2 text-sm font-semibold hover:bg-white/[0.04] transition flex items-center gap-2"
                >
                  <Edit3 size={14} /> {editing ? "Cancel" : "Edit profile"}
                </button>
              ) : (
                <button
                  data-testid="follow-toggle-btn"
                  onClick={toggleFollow}
                  className={`rounded-md px-4 py-2 text-sm font-semibold flex items-center gap-2 transition ${
                    profile.is_following ? "surface hover:bg-white/[0.04]" : "cta-green"
                  }`}
                >
                  {profile.is_following ? <><Check size={14} strokeWidth={2.5} /> Following</> : <><UserPlus size={14} strokeWidth={2.5} /> Follow</>}
                </button>
              )}
            </div>
          </div>

          {editing ? (
            <div className="space-y-3 mt-3">
              <input
                data-testid="edit-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Display name"
                className="w-full bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#00E676]/40"
              />
              <textarea
                data-testid="edit-bio"
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                placeholder="Your one-liner. What are you building?"
                rows={2}
                className="w-full bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#00E676]/40"
              />
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-white/40 mb-2">Niches</div>
                <div className="flex flex-wrap gap-1.5">
                  {NICHES.map((n) => (
                    <button
                      key={n}
                      data-testid={`edit-niche-${n}`}
                      onClick={() => toggleNiche(n)}
                      className={`text-[10px] font-mono uppercase tracking-wider px-2.5 py-1.5 rounded-md border transition ${
                        form.niches.includes(n)
                          ? "border-[#00E676] text-[#00E676] bg-[#00E676]/8"
                          : "border-white/8 text-white/40 hover:text-white"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              <div className="surface-elev rounded-md p-3 flex items-center gap-3">
                <input
                  data-testid="edit-mentor"
                  type="checkbox"
                  checked={form.is_mentor}
                  onChange={(e) => setForm({ ...form, is_mentor: e.target.checked })}
                  className="accent-[#00E676] w-4 h-4"
                />
                <label className="text-sm flex-1">Available as mentor</label>
                {form.is_mentor && (
                  <input
                    data-testid="edit-mentor-specialty"
                    value={form.mentor_specialty}
                    onChange={(e) => setForm({ ...form, mentor_specialty: e.target.value })}
                    placeholder="Specialty"
                    className="bg-white/[0.04] border border-white/8 rounded-md px-2 py-1 text-xs flex-1"
                  />
                )}
              </div>
              <button data-testid="edit-save" onClick={save} className="cta-green rounded-md px-5 py-2 text-sm font-bold">
                Save
              </button>
            </div>
          ) : (
            <p className="text-white/75 mt-3 leading-relaxed text-[15px]">{profile.bio || "No bio yet."}</p>
          )}

          <div className="grid grid-cols-4 gap-3 mt-6 pt-5 border-t border-white/5">
            <BigStat label="Verified earnings" value={`$${Math.round(verifiedEarnings).toLocaleString()}`} accent />
            <BigStat label="Posts" value={profile.posts_count || 0} />
            <BigStat label="Followers" value={profile.followers?.length || 0} />
            <BigStat label="Following" value={profile.following?.length || 0} />
          </div>

          {(profile.badges?.length || 0) > 0 && (
            <div className="mt-5 pt-5 border-t border-white/5">
              <div className="text-[10px] font-mono uppercase tracking-wider text-white/40 mb-2">Badges</div>
              <div className="flex flex-wrap gap-2">
                {profile.badges.map((b) => (
                  <span key={b} className="surface-elev rounded-md px-3 py-1.5 text-xs flex items-center gap-1.5">
                    <Award size={12} className="text-[#F4E8C1]" /> {b}
                  </span>
                ))}
              </div>
            </div>
          )}
        </motion.div>

        <h2 className="font-heading font-bold text-xl tracking-tight mb-4">Activity</h2>
        {posts.length === 0 ? (
          <div className="surface rounded-xl p-10 text-center text-sm text-white/50">No posts yet.</div>
        ) : (
          <div className="space-y-4">
            {posts.map((p) => (
              <PostCard
                key={p.post_id}
                post={p}
                onDeleted={(id) => setPosts((prev) => prev.filter((x) => x.post_id !== id))}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

const BigStat = ({ label, value, accent }) => (
  <div>
    <div className={`font-mono text-2xl tracking-tighter font-semibold ${accent ? "text-[#00E676]" : "text-white"}`}>
      {value}
    </div>
    <div className="text-[10px] font-mono uppercase tracking-wider text-white/40 mt-0.5">{label}</div>
  </div>
);
